/**
 * La pestaña «Mis objetos»: ver, mantener e importar lo que guardó el usuario.
 *
 * Daniel la pidió el 12-oct-2026 con el diagnóstico puesto: «tiene potencial,
 * pero le hace falta más visibilidad al usuario para poder profundizar». Hasta
 * hoy guardar una pieza era una acción escondida en un popover, y lo guardado
 * sólo reaparecía mezclado entre las de fábrica, con un tachito de 5 mm en la
 * esquina como único mantenimiento. Se podía crear y destruir; no se podía ni
 * ver la colección propia ni corregir nada.
 *
 * Va como ÚLTIMA pestaña de la misma vitrina y no como pantalla aparte —Daniel
 * eligió esa de las dos opciones— porque administrar los objetos y usarlos es la
 * misma tarea con dos minutos de diferencia: se nota que una pieza quedó con la
 * medida equivocada JUSTO cuando se la va a poner.
 *
 * La diferencia con las otras pestañas es el verbo: las de grupo son para USAR
 * —se arrastra, se arma, se pone— y ésta es para MANTENER. Por eso una pieza
 * propia aparece en las dos: en su grupo para ponerla, acá para arreglarla.
 */
import { useRef, useState } from 'react';
import { FilePlus, PencilSimple, Trash, Warning } from '@phosphor-icons/react';

import {
  GRUPO_PROPIO, MiniaturaMueble, borrarPropio, editarPropio, guardarPropio,
  leerCompartidos, leerPropios,
} from './muebles';
import type { ModeloMueble } from './muebles';
import { SelectorGrupo } from './SelectorGrupo';
import { desdeArchivo } from './importar-simbolo';
import type { Trazo } from './importar-simbolo';

/** El nombre de la pestaña. Vive acá porque la vitrina y el banco lo comparten. */
export const TAB_MIS_OBJETOS = 'Mis objetos';

const cm = (v: number) => Math.round(v * 100);

/** Lo que se está editando: una pieza existente o una importada todavía sin guardar. */
type Borrador = {
  clave: string | null;
  nombre: string;
  /** En CENTÍMETROS, que es como el usuario piensa una medida de mueble. */
  w: string;
  h: string;
  grupo: string;
  trazo?: Trazo;
};

/** Medida de arranque de un dibujo importado: 1 m en el lado largo. */
function medidaInicial(t: Trazo): { w: number; h: number } {
  const [, , cw, ch] = t.caja;
  // Se respeta la proporción del archivo SOLO acá, al proponer: es la mejor
  // pista que hay sobre qué dibujó el usuario. A partir de que él escribe una
  // medida manda la suya, y el dibujo se estira a lo que diga.
  const k = Math.max(cw, ch) || 1;
  return { w: Math.max(0.05, cw / k), h: Math.max(0.05, ch / k) };
}

export function MisObjetos({ onCambio, onPublicar }: {
  /**
   * Avisa que el catálogo propio cambió.
   *
   * No es cortesía: las piezas YA PUESTAS en el plano leen su nombre y su
   * símbolo del índice por clave, así que cambiar una pieza acá tiene que
   * repintar el lienzo. Sin esto, corregir una medida se ve en la lista y no en
   * el plano hasta la próxima vez que algo más lo repinte.
   */
  onCambio: (aviso: string) => void;
  /**
   * Publicar la pieza para TODOS, si el usuario tiene el rol.
   *
   * Llega sin poner mientras el catálogo compartido no exista: el botón no se
   * dibuja en vez de dibujarse muerto. Un botón que no hace nada enseña que la
   * función no sirve, y después nadie vuelve a tocarlo cuando empieza a servir.
   */
  onPublicar?: (clave: string) => void;
}) {
  // Las piezas se releen en cada repintado en vez de guardarse en estado: la
  // fuente es `localStorage` y la escriben también el popover de guardar y la
  // vitrina. Un estado propio acá sería una segunda copia que se desincroniza.
  const [tick, setTick] = useState(0);
  const propios = leerPropios();
  const [bo, setBo] = useState<Borrador | null>(null);
  const [queja, setQueja] = useState<string | null>(null);
  const [leyendo, setLeyendo] = useState(false);
  const archivo = useRef<HTMLInputElement>(null);
  void tick;

  const refrescar = () => setTick((k) => k + 1);

  async function importar(f: File | undefined) {
    if (!f) return;
    setQueja(null);
    setLeyendo(true);
    try {
      const t = await desdeArchivo(f);
      const { w, h } = medidaInicial(t);
      setBo({
        clave: null, trazo: t, grupo: GRUPO_PROPIO,
        // El nombre del archivo es la mejor primera propuesta: quien exportó
        // «lavatorio-ovalado.svg» ya le puso nombre a la pieza.
        nombre: f.name.replace(/\.(svg|pdf)$/i, '').replace(/[-_]+/g, ' ').trim().slice(0, 40),
        w: String(cm(w)), h: String(cm(h)),
      });
    } catch (e) {
      setQueja(e instanceof Error ? e.message : 'No se pudo leer el archivo.');
    } finally {
      setLeyendo(false);
      // El input se limpia o elegir DOS VECES el mismo archivo no dispara el
      // segundo `change`, y el usuario ve que su clic no hizo nada.
      if (archivo.current) archivo.current.value = '';
    }
  }

  function guardar() {
    if (!bo) return;
    const w = Number(bo.w.replace(',', '.')) / 100;
    const h = Number(bo.h.replace(',', '.')) / 100;
    const nombre = bo.nombre.trim();
    if (!nombre) { setQueja('Ponele un nombre.'); return; }
    if (!(w > 0) || !(h > 0)) { setQueja('Las dos medidas tienen que ser mayores que cero.'); return; }
    const grupo = bo.grupo.trim() || GRUPO_PROPIO;
    if (bo.clave) editarPropio(bo.clave, { nombre, w, h, grupo });
    else guardarPropio({ nombre, w, h, grupo, simbolo: 'mueble', trazo: bo.trazo });
    setBo(null);
    setQueja(null);
    refrescar();
    onCambio(bo.clave
      ? `«${nombre}» quedó actualizado.`
      : `«${nombre}» quedó en el catálogo, bajo ${grupo}. Sólo en este dispositivo.`);
  }

  function borrar(m: ModeloMueble) {
    // Se pregunta porque no hay deshacer para esto: el catálogo propio no entra
    // en la pila de deshacer del plano, que es de la planta y no del usuario.
    if (!confirm(`¿Quitar «${m.nombre}» de tus objetos?`)) return;
    borrarPropio(m.clave);
    if (bo?.clave === m.clave) setBo(null);
    refrescar();
    onCambio('La pieza salió del catálogo. Las que ya están en el plano se quedan.');
  }

  // Las que YA están publicadas. Se marcan en vez de ofrecer otra vez el botón
  // porque publicar dos veces no hace nada y el botón vivo haría pensar que la
  // primera no funcionó.
  const publicadas = new Set(leerCompartidos().map((m) => m.clave));

  return (
    <div className="max-h-[46vh] overflow-y-auto px-0.5 pt-1" data-mis-objetos="">
      <button
        type="button"
        onClick={() => archivo.current?.click()}
        disabled={leyendo}
        data-importar-objeto=""
        className="flex w-full items-center justify-center gap-1.5 rounded border
                   border-dashed border-[#5f6f80] px-2 py-2 text-[0.7rem] font-medium
                   text-[#5f6f80] hover:bg-stone-100 active:bg-stone-200
                   disabled:opacity-50"
      >
        <FilePlus size={14} weight="bold" />
        {leyendo ? 'Leyendo el dibujo…' : 'Importar dibujo (SVG o PDF)'}
      </button>
      <input
        ref={archivo} type="file" accept=".svg,.pdf,image/svg+xml,application/pdf"
        className="hidden" data-archivo-objeto=""
        onChange={(e) => void importar(e.target.files?.[0])}
      />

      {queja && (
        <p className="mt-1 flex items-start gap-1 rounded bg-red-50 px-1.5 py-1
                      text-[0.65rem] leading-tight text-red-700"
           data-queja-objeto="">
          <Warning size={12} weight="bold" className="mt-px shrink-0" />
          {queja}
        </p>
      )}

      {/* El formulario sirve a los dos caminos —importar algo nuevo y corregir
          algo viejo— porque los campos son exactamente los mismos. Separarlos
          en dos pantallas duplicaría la validación para no ganar nada. */}
      {bo && (
        <div className="mt-1 rounded border border-stone-300 bg-stone-50 p-1.5"
             data-forma-objeto={bo.clave ?? 'nuevo'}>
          <div className="flex items-center gap-1.5">
            {bo.trazo && (
              // El dibujo importado se ve ANTES de guardarlo, encuadrado en la
              // medida que se está escribiendo: es la única manera de darse
              // cuenta de que el PDF trajo media página en vez del mueble.
              <MiniaturaMueble
                px={40}
                modelo={{
                  clave: 'previa', nombre: bo.nombre, grupo: bo.grupo, simbolo: 'mueble',
                  trazo: bo.trazo,
                  w: Math.max(0.01, Number(bo.w.replace(',', '.')) / 100 || 0.01),
                  h: Math.max(0.01, Number(bo.h.replace(',', '.')) / 100 || 0.01),
                }}
              />
            )}
            <input
              autoFocus
              value={bo.nombre}
              onChange={(e) => setBo({ ...bo, nombre: e.target.value })}
              placeholder="Cómo se llama"
              data-nombre-objeto=""
              className="w-full rounded border border-stone-300 px-1.5 py-1 text-xs
                         outline-none focus:border-[#5f6f80]"
            />
          </div>
          <div className="mt-1 flex items-center gap-1">
            {([['w', 'Ancho'], ['h', 'Fondo']] as const).map(([k, rotulo]) => (
              <label key={k} className="flex flex-1 items-center gap-1
                                        text-[0.6rem] text-stone-500">
                {rotulo}
                <input
                  value={bo[k]}
                  inputMode="numeric"
                  onChange={(e) => setBo({ ...bo, [k]: e.target.value })}
                  data-medida-objeto={k}
                  className="w-full rounded border border-stone-300 px-1 py-1 text-xs
                             tabular-nums outline-none focus:border-[#5f6f80]"
                />
                cm
              </label>
            ))}
          </div>
          <div className="mt-1">
            <SelectorGrupo
              id="mis" valor={bo.grupo} onAviso={onCambio}
              onCambio={(g) => setBo({ ...bo, grupo: g })}
            />
          </div>
          <div className="mt-1.5 flex gap-1">
            <button
              type="button" onClick={guardar} data-guardar-objeto=""
              className="flex-1 rounded bg-[#5f6f80] px-2 py-1 text-[0.7rem]
                         font-medium text-white hover:bg-[#4d5c6b]"
            >
              Guardar
            </button>
            <button
              type="button" onClick={() => { setBo(null); setQueja(null); }}
              data-cancelar-objeto=""
              className="rounded border border-stone-300 px-2 py-1 text-[0.7rem]
                         text-stone-600 hover:bg-stone-100"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {!propios.length && !bo && (
        <p className="px-1 py-3 text-center text-[0.65rem] leading-snug text-stone-400">
          Todavía no guardaste ningún objeto.
          <br />
          Importá un dibujo, o ajustá una pieza en el plano y guardala desde su paleta.
        </p>
      )}

      <ul className="mt-1">
        {propios.map((m) => (
          <li key={m.clave}
              className="flex items-center gap-1.5 rounded px-1 py-1 hover:bg-stone-100"
              data-objeto-propio={m.clave}>
            <MiniaturaMueble modelo={m} px={28} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[0.68rem] leading-tight text-stone-700">
                {m.nombre}
              </span>
              <span className="block text-[0.58rem] leading-none tabular-nums text-stone-400">
                {cm(m.w)}×{cm(m.h)} · {m.grupo}
              </span>
            </span>
            {publicadas.has(m.clave) ? (
              <span className="rounded bg-stone-200 px-1.5 py-0.5 text-[0.55rem]
                               font-medium text-stone-500"
                    data-objeto-publicado={m.clave}
                    title="Esta pieza la ve todo el equipo">
                De todos
              </span>
            ) : onPublicar && (
              <button
                type="button" onClick={() => onPublicar(m.clave)}
                data-publicar-objeto={m.clave}
                className="rounded px-1.5 py-1 text-[0.6rem] font-medium text-[#5f6f80]
                           hover:bg-stone-200"
                title="Publicar para todo el equipo"
              >
                Publicar
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setQueja(null);
                setBo({
                  clave: m.clave, nombre: m.nombre, grupo: m.grupo,
                  w: String(cm(m.w)), h: String(cm(m.h)), trazo: m.trazo,
                });
              }}
              data-editar-objeto={m.clave}
              aria-label={`Editar ${m.nombre}`}
              className="rounded p-1 text-stone-400 hover:bg-stone-200 hover:text-stone-700"
            >
              <PencilSimple size={13} weight="bold" />
            </button>
            <button
              type="button" onClick={() => borrar(m)}
              data-borrar-objeto={m.clave}
              aria-label={`Quitar ${m.nombre}`}
              className="rounded p-1 text-stone-400 hover:bg-stone-200 hover:text-stone-700"
            >
              <Trash size={13} weight="bold" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
